import StripeCheckoutForm from "components/Payment/StripeCheckoutForm";
import { GetPaymentPlans } from "components/Payment/PaymentApi";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useState } from "react";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PageLayout from "examples/LayoutContainers/PageLayout";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import ProductsList from "./ProductsList";
import {
  getStoredCheckoutPlanId,
  persistCheckoutPlan,
} from "utils/checkoutFlow";
import { APP_DASHBOARD_ROUTE } from "utils/routes";

const stripePromise = process.env.REACT_APP_STRIPE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_KEY)
  : null;

const isStandardPlan = (plan) =>
  String(plan?.slug || "").toLowerCase() === "standard" || Number(plan?.tierId) === 2;

const getCheckoutDisplayPlan = (plan) => {
  if (!plan) {
    return plan;
  }

  if (String(plan.slug || "").toLowerCase() === "proanual") {
    return {
      ...plan,
      name: "Pro Anual",
    };
  }

  return plan;
};

const buildCheckoutPlans = (plans = []) =>
  (Array.isArray(plans) ? plans : [])
    .filter((plan) => !isStandardPlan(plan))
    .map(getCheckoutDisplayPlan);

function Payment() {
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const location = useLocation();
  const hasAuthToken =
    typeof window !== "undefined" && Boolean(window.localStorage.getItem("tradez-token"));

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const data = await GetPaymentPlans();
        if (!isMounted) {
          return;
        }

        setPlans(buildCheckoutPlans(data));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setLoadError(error.message);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (plans.length === 0) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const planFromQuery = params.get("plan") || params.get("plano");
    const savedPlan = getStoredCheckoutPlanId();
    const preferredPlan = plans.find(
      (item) =>
        item.slug === planFromQuery ||
        item.slug === savedPlan ||
        String(item.tierId) === planFromQuery ||
        String(item.tierId) === savedPlan
    );
    const fallbackPlan =
      plans.find((item) => item.highlight) ||
      plans.find((item) => item.checkoutEnabled) ||
      plans[0];

    setSelectedPlanId((currentValue) => currentValue ?? preferredPlan?.tierId ?? fallbackPlan?.tierId ?? null);
  }, [location.search, plans]);

  useEffect(() => {
    const selectedPlan = plans.find((plan) => plan.tierId === selectedPlanId);
    if (!selectedPlan) {
      return;
    }

    persistCheckoutPlan(selectedPlan);
  }, [plans, selectedPlanId]);

  const selectedPlan = plans.find((plan) => plan.tierId === selectedPlanId) ?? null;
  const stripeOptions = selectedPlan?.checkoutEnabled
    ? {
        locale: "pt-BR",
        mode: "payment",
        amount: selectedPlan.priceInCents,
        currency: selectedPlan.currencyCode,
        appearance: {
          theme: "flat",
          variables: {
            colorPrimary: "#0f766e",
            colorBackground: "#ffffff",
            colorText: "#0f172a",
            colorDanger: "#dc2626",
            fontFamily: "Golos Text, sans-serif",
            spacingUnit: "4px",
            borderRadius: "14px",
          },
          rules: {
            ".Input": {
              border: "1px solid rgba(148, 163, 184, 0.35)",
              boxShadow: "none",
            },
            ".Tab": {
              border: "1px solid rgba(148, 163, 184, 0.24)",
            },
            ".Tab--selected": {
              borderColor: "#0f766e",
            },
          },
        },
      }
    : null;

  const handleRetry = async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const data = await GetPaymentPlans();
      setPlans(buildCheckoutPlans(data));
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayout background="default">
      <StyledPage>
        <HeroSection>
          <HeroText>
            <Eyebrow>Checkout Patrimônio360</Eyebrow>
            <HeroTitle>Escolha o plano para remover limites e liberar a camada Pro da carteira.</HeroTitle>
            <HeroDescription>
              Orçamento, metas e fluxo de caixa já começam no grátis. Compare os benefícios, confirme o
              plano ideal e conclua o pagamento para ganhar mais capacidade e, quando fizer sentido, os
              recursos avançados de alocação da carteira.
            </HeroDescription>
          </HeroText>

          <HighlightsGrid>
            <HighlightCard>
              <HighlightIcon>
                <TaskAltRoundedIcon fontSize="inherit" />
              </HighlightIcon>
              <HighlightText>
                Base grátis com caixa, metas e orçamento desde o início
              </HighlightText>
            </HighlightCard>
            <HighlightCard>
              <HighlightIcon>
                <LockRoundedIcon fontSize="inherit" />
              </HighlightIcon>
              <HighlightText>
                Pagamento protegido pela Stripe
              </HighlightText>
            </HighlightCard>
            <HighlightCard>
              <HighlightIcon>
                <AutoGraphRoundedIcon fontSize="inherit" />
              </HighlightIcon>
              <HighlightText>
                Planos pagos para remover limites e destravar o Pro
              </HighlightText>
            </HighlightCard>
          </HighlightsGrid>
        </HeroSection>

        <MainGrid>
          <LeftColumn>
            <SectionHeader>
              <SectionStep>Passo 1</SectionStep>
              <SectionTitle>Escolha o plano ideal para a sua evolução dentro do produto</SectionTitle>
            </SectionHeader>

            {loadError && (
              <InlineFeedback $tone="error">
                <span>{loadError}</span>
                <RetryButton type="button" onClick={handleRetry}>
                  Tentar novamente
                </RetryButton>
              </InlineFeedback>
            )}

            {isLoading ? (
              <LoadingCard>Carregando planos...</LoadingCard>
            ) : (
              <ProductsList
                plans={plans}
                selectedPlanId={selectedPlanId}
                onSelectPlan={setSelectedPlanId}
              />
            )}
          </LeftColumn>

          <RightColumn>
            <SummaryCard>
              <SectionStep>Resumo</SectionStep>
              {selectedPlan ? (
                <>
                  <SummaryTitle>{selectedPlan.name}</SummaryTitle>
                  <SummaryPrice>{selectedPlan.formattedPrice}</SummaryPrice>
                  <SummaryMeta>
                    <span>{selectedPlan.billingLabel}</span>
                    <span>{selectedPlan.renewalLabel}</span>
                  </SummaryMeta>
                  <SummaryDescription>{selectedPlan.description}</SummaryDescription>

                  <SummaryList>
                    {selectedPlan.features.map((feature) => (
                      <SummaryItem key={feature}>
                        <TaskAltRoundedIcon fontSize="inherit" />
                        <span>{feature}</span>
                      </SummaryItem>
                    ))}
                  </SummaryList>

                  <TrustList>
                    <TrustItem>
                      <LockRoundedIcon fontSize="inherit" />
                      <span>Pagamento processado pela Stripe com exibição segura dos dados.</span>
                    </TrustItem>
                    <TrustItem>
                      <ArrowForwardRoundedIcon fontSize="inherit" />
                      <span>O plano é atualizado logo após a confirmação do pagamento.</span>
                    </TrustItem>
                  </TrustList>
                </>
              ) : (
                <EmptyState>Selecione um plano para visualizar o resumo do pedido.</EmptyState>
              )}
            </SummaryCard>

            <CheckoutCard>
              <SectionHeader>
                <SectionStep>Passo 2</SectionStep>
                <SectionTitle>Finalize seu pagamento</SectionTitle>
                <SectionDescription>
                  Antes de pagar, confira se o plano selecionado corresponde exatamente ao nível de
                  capacidade e de recursos avançados que você quer liberar. Se você ainda não tiver conta,
                  poderá informar seu e-mail e resolver o acesso logo depois do pagamento.
                </SectionDescription>
              </SectionHeader>

              {!selectedPlan && <EmptyState>Escolha um plano para habilitar o checkout.</EmptyState>}

              {selectedPlan && !selectedPlan.checkoutEnabled && (
                <FreePlanCard>
                  <FreePlanTitle>Esse plano não exige pagamento.</FreePlanTitle>
                  <FreePlanDescription>
                    Se você quiser continuar no plano gratuito, basta seguir para o painel e usar a
                    plataforma para organizar patrimônio, receitas, despesas, orçamento e metas dentro dos limites atuais.
                  </FreePlanDescription>
                  <FreePlanButton href={APP_DASHBOARD_ROUTE}>Ir para o painel</FreePlanButton>
                </FreePlanCard>
              )}

              {selectedPlan?.checkoutEnabled && !stripePromise && (
                <InlineFeedback $tone="error">
                  A chave pública do Stripe não está configurada no frontend.
                </InlineFeedback>
              )}

              {selectedPlan?.checkoutEnabled && stripePromise && stripeOptions && (
                <Elements
                  key={`checkout-${selectedPlan.tierId}`}
                  stripe={stripePromise}
                  options={stripeOptions}
                >
                  <StripeCheckoutForm plan={selectedPlan} isAuthenticated={hasAuthToken} />
                </Elements>
              )}
            </CheckoutCard>
          </RightColumn>
        </MainGrid>
      </StyledPage>
    </PageLayout>
  );
}

const StyledPage = styled.div`
  min-height: 100vh;
  padding: 2rem 1rem 4rem;
  background:
    radial-gradient(circle at top left, rgba(240, 186, 141, 0.22), transparent 28%),
    radial-gradient(circle at top right, rgba(15, 118, 110, 0.12), transparent 24%),
    linear-gradient(180deg, #f7fafc 0%, #eef5f6 100%);

  @media (min-width: 768px) {
    padding: 3rem 2rem 4rem;
  }
`;

const HeroSection = styled.section`
  max-width: 1180px;
  margin: 0 auto 1.75rem;
  padding: 2rem;
  border-radius: 32px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.92)),
    #0f172a;
  color: #ffffff;
  box-shadow: 0 28px 60px rgba(15, 23, 42, 0.2);
`;

const HeroText = styled.div`
  max-width: 720px;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  margin: 1rem 0 0.8rem;
  font-size: clamp(2rem, 4vw, 3.4rem);
  line-height: 1.05;
`;

const HeroDescription = styled.p`
  max-width: 620px;
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.02rem;
  line-height: 1.7;
`;

const HighlightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.9rem;
  margin-top: 1.5rem;
`;

const HighlightCard = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.75rem;
  align-items: center;
  padding: 1rem 1.1rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
`;

const HighlightIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 1.2rem;
`;

const HighlightText = styled.span`
  font-weight: 700;
  line-height: 1.4;
`;

const MainGrid = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  gap: 1.5rem;

  @media (min-width: 1080px) {
    grid-template-columns: minmax(0, 1.4fr) minmax(360px, 0.95fr);
    align-items: start;
  }
`;

const LeftColumn = styled.section`
  display: grid;
  gap: 1.25rem;
`;

const RightColumn = styled.aside`
  display: grid;
  gap: 1.25rem;

  @media (min-width: 1080px) {
    position: sticky;
    top: 1.5rem;
  }
`;

const cardShell = `
  padding: 1.5rem;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
`;

const SummaryCard = styled.div`
  ${cardShell}
  display: grid;
  gap: 1rem;
`;

const CheckoutCard = styled.div`
  ${cardShell}
  display: grid;
  gap: 1rem;
`;

const SectionHeader = styled.div`
  display: grid;
  gap: 0.55rem;
`;

const SectionStep = styled.span`
  color: #0f766e;
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #0f172a;
  font-size: 1.5rem;
  line-height: 1.2;
`;

const SectionDescription = styled.p`
  margin: 0;
  color: #64748b;
  line-height: 1.6;
`;

const InlineFeedback = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.9rem;
  padding: 1rem 1.1rem;
  border-radius: 18px;
  border: 1px solid ${({ $tone }) => ($tone === "error" ? "rgba(220, 38, 38, 0.2)" : "rgba(15, 118, 110, 0.2)")};
  background: ${({ $tone }) => ($tone === "error" ? "rgba(220, 38, 38, 0.08)" : "rgba(15, 118, 110, 0.08)")};
  color: ${({ $tone }) => ($tone === "error" ? "#991b1b" : "#115e59")};
  font-weight: 600;
`;

const RetryButton = styled.button`
  min-height: 2.75rem;
  padding: 0 1rem;
  border: 0;
  border-radius: 999px;
  background: #0f766e;
  color: #ffffff;
  font-weight: 700;
  cursor: pointer;
`;

const LoadingCard = styled.div`
  ${cardShell}
  color: #475569;
  font-weight: 600;
`;

const SummaryTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 1.55rem;
`;

const SummaryPrice = styled.div`
  font-size: 2.4rem;
  font-weight: 800;
  color: #111827;
`;

const SummaryMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  color: #475569;
  font-weight: 600;
`;

const SummaryDescription = styled.p`
  margin: 0;
  color: #475569;
  line-height: 1.6;
`;

const SummaryList = styled.ul`
  display: grid;
  gap: 0.8rem;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const SummaryItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.7rem;
  align-items: flex-start;
  color: #0f172a;
  line-height: 1.5;

  svg {
    margin-top: 0.2rem;
    color: #0f766e;
    font-size: 1rem;
  }
`;

const TrustList = styled.div`
  display: grid;
  gap: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
`;

const TrustItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.65rem;
  align-items: flex-start;
  color: #475569;
  line-height: 1.5;

  svg {
    margin-top: 0.2rem;
    color: #0f766e;
    font-size: 1rem;
  }
`;

const EmptyState = styled.div`
  padding: 1rem;
  border-radius: 18px;
  background: rgba(15, 118, 110, 0.06);
  color: #115e59;
  line-height: 1.6;
`;

const FreePlanCard = styled.div`
  display: grid;
  gap: 0.9rem;
  padding: 1.15rem;
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(255, 248, 235, 0.9), rgba(255, 255, 255, 0.98));
  border: 1px solid rgba(240, 186, 141, 0.4);
`;

const FreePlanTitle = styled.h3`
  margin: 0;
  color: #9a3412;
`;

const FreePlanDescription = styled.p`
  margin: 0;
  color: #7c2d12;
  line-height: 1.6;
`;

const FreePlanButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
  padding: 0 1.25rem;
  width: fit-content;
  border-radius: 14px;
  background: #0f766e;
  color: #ffffff;
  font-weight: 700;
  text-decoration: none;
`;

export default Payment;
